import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import { formatPageTitle, formatDescription, buildOrganizationSchema, buildWebSiteSchema, buildWebPageSchema, buildSchemaGraph } from './utils/seo';
import { useDarkMode } from './DarkModeContext';
import { trackEvent } from './utils/amplitude';
import { getCurrencySymbol } from './utils/currency';
import './LandingPage1.css';
import Footer from './Footer';

function Pricing() {
    const { darkMode } = useDarkMode();
    const currency = getCurrencySymbol();
    
    const handleGoogleSignup = () => {
        console.log('Redirecting to Google Sign-In...');
        trackEvent('button_clicked', { buttonName: 'Google Signup', page: 'Pricing' });
        
        // Determine backend URL based on environment
        const backendUrl = process.env.NODE_ENV === 'production' 
            ? 'https://proto-backend-d2c1638486b9.herokuapp.com'
            : 'http://localhost:5000';
            
        window.location.href = `${backendUrl}/auth/google`; // Backend Google auth route
    };
    
    return (
        <>
        <Helmet>
            <title>{formatPageTitle('Merra AI Pricing – Plans for Creators & Teams')}</title>
            <meta name="description" content={formatDescription('Compare Merra AI plans for creators and teams. Flexible pricing with a free trial—no credit card required. Scale video output without hiring editors.')} />
            <meta name="keywords" content="Merra AI pricing, video creation pricing, AI video tool pricing, Merra AI plans, video editing cost" />
            <link rel="canonical" href="https://www.merra.ai/pricing" />
            <meta property="og:title" content={formatPageTitle('Merra AI Pricing – Plans for Creators & Teams')} />
            <meta property="og:description" content={formatDescription('Compare Merra AI plans for creators and teams. Flexible pricing with a free trial—no credit card required.')} />
            <meta property="og:url" content="https://www.merra.ai/pricing" />
        </Helmet>
        <JsonLd
            schema={buildSchemaGraph([
                buildOrganizationSchema(),
                buildWebSiteSchema(),
                buildWebPageSchema({
                    name: formatPageTitle('Merra AI Pricing – Plans for Creators & Teams'),
                    description: 'Compare Merra AI plans for creators and teams.',
                    url: 'https://www.merra.ai/pricing',
                }),
            ])}
        />
        <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ 
                color: darkMode ? '#ffffff' : '#2c3e50', 
                borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, 
                paddingBottom: '10px',
                textAlign: 'center',
                marginBottom: '40px'
            }}>
                Pricing
            </h1>

            <div style={{ 
                textAlign: 'center', 
                marginBottom: '40px',
                fontSize: '18px',
                color: darkMode ? '#e0e0e0' : '#666'
            }}>
                The first 3 videos are free, no credit card required.
            </div>

            {/* Pricing Plans */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: '20px', 
                marginBottom: '50px' 
            }}>
                {/* Free Plan */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff', 
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`, 
                    borderRadius: '8px', 
                    padding: '30px',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'default'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <h3 style={{ 
                        marginTop: 0, 
                        marginBottom: '15px', 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        fontSize: '24px'
                    }}>
                        Free
                    </h3>
                    <div style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '20px', 
                        color: darkMode ? '#4A9EFF' : '#1976d2'
                    }}>
                        {currency}0<span style={{ fontSize: '18px', fontWeight: 'normal' }}>/month</span>
                    </div>
                    <ul style={{ 
                        textAlign: 'left', 
                        fontSize: '14px', 
                        color: darkMode ? '#e0e0e0' : '#666', 
                        margin: 0, 
                        paddingLeft: '20px', 
                        listStyleType: 'disc', 
                        lineHeight: '2',
                        marginBottom: '20px'
                    }}>
                        <li>3 videos per month</li>
                        <li>Import raw content</li>
                        <li>Generate hooks, scripts, voiceovers</li>
                        <li>Generate videos</li>
                    </ul>
                </div>

                {/* Creator Plan */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff', 
                    border: `2px solid #AB5898`, 
                    borderRadius: '8px', 
                    padding: '30px',
                    textAlign: 'center',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'default'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <div style={{ 
                        position: 'absolute', 
                        top: '-12px', 
                        left: '50%', 
                        transform: 'translateX(-50%)', 
                        backgroundColor: '#AB5898', 
                        color: 'white', 
                        padding: '4px 12px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        POPULAR
                    </div>
                    <h3 style={{ 
                        marginTop: '12px', 
                        marginBottom: '15px', 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        fontSize: '24px'
                    }}>
                        Creator
                    </h3>
                    <div style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '20px', 
                        color: darkMode ? '#4A9EFF' : '#1976d2'
                    }}>
                        {currency}25<span style={{ fontSize: '18px', fontWeight: 'normal' }}>/month</span>
                    </div>
                    <ul style={{ 
                        textAlign: 'left', 
                        fontSize: '14px', 
                        color: darkMode ? '#e0e0e0' : '#666', 
                        margin: 0, 
                        paddingLeft: '20px', 
                        listStyleType: 'disc', 
                        lineHeight: '2',
                        marginBottom: '20px'
                    }}>
                        <li><span style={{ textDecoration: 'line-through' }}>20 videos per month</span> Unlimited videos</li>
                        <li>Import raw content</li>
                        <li>Generate hooks, scripts, voiceovers</li>
                        <li>Generate videos</li>
                        <li>50GB storage (for raw videos)</li>
                    </ul>
                </div>
                
                {/* Pro Plan */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#ffffff', 
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`, 
                    borderRadius: '8px', 
                    padding: '30px',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'default'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <h3 style={{ 
                        marginTop: 0, 
                        marginBottom: '15px', 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        fontSize: '24px'
                    }}>
                        Pro
                    </h3>
                    <div style={{ 
                        fontSize: '36px', 
                        fontWeight: 'bold', 
                        marginBottom: '20px', 
                        color: '#AB5898'
                    }}>
                        {currency}99<span style={{ fontSize: '18px', fontWeight: 'normal' }}>/month</span>
                    </div>
                    <ul style={{ 
                        textAlign: 'left', 
                        fontSize: '14px', 
                        color: darkMode ? '#e0e0e0' : '#666', 
                        margin: 0, 
                        paddingLeft: '20px', 
                        listStyleType: 'disc', 
                        lineHeight: '2',
                        marginBottom: '20px'
                    }}>
                        <li>150 videos per month</li>
                        <li>Import raw content</li>
                        <li>Generate hooks, scripts, voiceovers</li>
                        <li>Generate videos</li>
                        <li style={{ color: '#AB5898' }}>200GB storage per profile (for raw videos)</li>
                        <li style={{ color: '#AB5898' }}>+ Invite team members</li>
                        <li style={{ color: '#AB5898' }}>+ Multiple brand profiles (+{currency}99 per profile)</li>
                    </ul>
                    <p> Become an infinite content machine for {currency}99/month</p>
                </div>
            </div>

            {/* Additional Information */}
            <div style={{ 
                marginTop: '50px', 
                padding: '30px',
                backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '8px',
                border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`
            }}>
                <h2 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    marginTop: 0,
                    marginBottom: '20px',
                    borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, 
                    paddingBottom: '10px' 
                }}>
                    Frequently Asked Questions
                </h2>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        Can I sign up for free?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        Yes! Sign up is completely free with no credit card required. You can generate 3 videos, then you can upgrade to a paid plan. 
                        We offer flexible pricing plans based on your needs. You can cancel at any time. Start your free trial today and see the difference Merra AI can make for your content creation.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        How does pricing scale with usage and seats?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        20 videos per month for Creator plan, and 150 videos per month for Pro plan, and unlimited seats in the Pro plan.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        Isn't it a bit expensive?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        It's a lot, because we want you to post a lot of videos. Paying more gives you an extra incentive to post more videos. If you only paid {currency}10 a month, you would be fine posting 1 video and you wouldn't have any result.
                        It's also much faster than any other cheaper tool (you save a ton of time if you usually edit your own videos, and this is valuable), and way cheaper than hiring a freelancer.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        Can I cancel anytime?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        Yes, you can cancel your subscription at any time. There are no long-term contracts or commitments.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        What payment methods do you accept?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        We accept all major credit cards through our secure payment processor, Stripe.
                    </p>
                </div>
            </div>

            {/* CTA Section */}
            <div style={{ 
                textAlign: 'center', 
                marginTop: '50px',
                padding: '40px',
                backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '8px',
                border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`
            }}>
                <h2 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    marginBottom: '20px' 
                }}>
                    Ready to get started?
                </h2>
                <p style={{ 
                    color: darkMode ? '#e0e0e0' : '#666', 
                    marginBottom: '30px',
                    fontSize: '18px'
                }}>
                    Start creating professional videos in minutes. No credit card required for your first 3 videos.
                </p>
                <button
                    onClick={handleGoogleSignup}
                    className='google-btn'
                    style={{justifySelf:'center'}}
                >
                    <div className="google-icon"/>
                    Continue with Google
                </button>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default Pricing;

